import { Grid, Stack, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { StructureLayoutProps } from './StructureLayout.props';
import {
  RoleBox,
  ContactGridContainer,
} from './StructureLayout.styles';
import { ScreenSize, SizeProps } from '../../../@types';
import { GRID_SPACING } from '../../../configs/ui-constants';
import { EMPTY_NAME_FIELD_MESSAGE } from '../../../constants/common';
import { getSiteSchemas } from '../../../utils/structure';
import { SelectMenu } from '../../atoms/SelectMenu';
import { TextField } from '../../atoms/TextField';
import { Typography, TypographyFontWeightProps, TypographyVariantProps } from '../../atoms/Typography';
import { Tags } from '../Tags';

export const StructureLayout: React.FC<StructureLayoutProps> = ({
  isNew,
  structureLayoutSchemaType,
  onChange,
  error,
  helperText,
  structureLayoutData,
  allTags,
  renderNotes=true,
  assignedSelectedTags,
  assignedNewTags,
  enableSiteSchemaType,
  enableReferenceNumbers,
  onChangeSelectedTags,
  onChangeNewTags,
  setHelperText,
  structureEditPermissions,
  isClientCustomerStructureCreation,
  selectedStructureType
}) => {
  const isMobileView = useMediaQuery(ScreenSize.MOBILE);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileView);
  }, [isMobileView]);
  
  return (
    <RoleBox>
      <ContactGridContainer container spacing={GRID_SPACING}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            required
            disabled={(!isNew && !structureEditPermissions?.hasNameEditPermission) || (isClientCustomerStructureCreation && (!selectedStructureType || selectedStructureType === '-1'))}
            error={!!helperText?.nameHelperText}
            helperText={helperText?.nameHelperText}
            label="Name"
            value={structureLayoutData.name}
            onChange={(structureName) => {
              onChange({
                ...structureLayoutData,
                name: structureName
              });

              setHelperText({
                ...helperText,
                nameHelperText: !structureName ? EMPTY_NAME_FIELD_MESSAGE : ''
              });
            }}
          />
        </Grid>
        {!isClientCustomerStructureCreation &&
          <Grid size={{ xs: 12 }}>
            <Tags
              selectedTags={assignedSelectedTags} 
              newTags={assignedNewTags} 
              setSeletedTags={onChangeSelectedTags} 
              setNewTags={onChangeNewTags}
              allTags={allTags}
              disabled={(!isNew && !structureEditPermissions?.hasTagsEditPermission) || (isClientCustomerStructureCreation && (!selectedStructureType || selectedStructureType === '-1'))}
            />
          </Grid>
        }
        {enableSiteSchemaType && <Grid size={{ xs: 12 }}>
          <SelectMenu
            size={SizeProps.Small}
            id="page-size-menu"
            minWidth="100%"
            items={getSiteSchemas(structureLayoutSchemaType)}
            selectedValue={structureLayoutData.siteSchemaId || '-1'}
            label="Site Schema"
            onChange={(schemaId) => {
              onChange({
                ...structureLayoutData,
                siteSchemaId: schemaId
              });
            }}
            optionalLabelEnabled={true}
          />
        </Grid>}
        {renderNotes && 
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            disabled={(!isNew && !structureEditPermissions?.hasNotesEditPermission) || (isClientCustomerStructureCreation && (!selectedStructureType || selectedStructureType === '-1'))}
            value={structureLayoutData?.notes}
            onChange={(text) => onChange({
              ...structureLayoutData,
              notes: text
            })}
          />
        </Grid>}
        {enableReferenceNumbers &&
          <>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                error={error}
                disabled={!isNew && !structureEditPermissions?.hasReference1EditPermission}
                label="Reference No 1"
                value={structureLayoutData?.referenceNo1}
                onChange={(text) => onChange({
                  ...structureLayoutData,
                  referenceNo1: text
                })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                error={error}
                disabled={!isNew && !structureEditPermissions?.hasReference2EditPermission}
                label="Reference No 2"
                value={structureLayoutData?.referenceNo2}
                onChange={(text) => onChange({
                  ...structureLayoutData,
                  referenceNo2: text
                })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                error={error}
                disabled={!isNew && !structureEditPermissions?.hasReference3EditPermission}
                label="Reference No 3"
                value={structureLayoutData?.referenceNo3}
                onChange={(text) => onChange({
                  ...structureLayoutData,
                  referenceNo3: text
                })} />
            </Grid>
          </>
        }
        {!isNew &&
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Zone"
              value={structureLayoutData?.zone}
              disabled
            />
          </Grid>
        }
        {!isNew &&
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Region"
              value={structureLayoutData?.region}
              disabled
            />
          </Grid>
        }
        {structureLayoutData?.mostRecentPresentation &&
          <Grid size={{ xs: 12 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="flex-start" mb={1}>
              <Typography variant={TypographyVariantProps.Body1} fontWeight={TypographyFontWeightProps.Bold}>Presentation: </Typography>
              <Typography variant={TypographyVariantProps.Subtitle1}>{structureLayoutData?.mostRecentPresentation?.description}</Typography>
            </Stack>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="center">
              <Typography variant={TypographyVariantProps.Body1} fontWeight={TypographyFontWeightProps.Bold}>Presentation Date: </Typography>
              <Typography variant={TypographyVariantProps.Subtitle1}>{structureLayoutData?.mostRecentPresentation?.date}</Typography>
            </Stack>
          </Grid>
        }
      </ContactGridContainer>
    </RoleBox>
  );
};
